import { type ReactNode, useEffect, useState, useMemo } from 'react'
import { groupBy } from 'lodash'
import { TextField } from 'shared/ui/TextField'
import { Typography } from 'shared/ui/Typography'
import styles from 'shared/ui/Actions/styles.module.scss'
import { Action } from 'shared/ui/Actions/Action'
import type { ActionItem } from 'shared/ui/Actions/Actions'
import { getAriaLabel } from 'shared/lib'

type ActionGroup = [string, ActionItem[]]

type Props = {
  setOnCloseMoreDropdown?: (fn: () => void) => void
  onCloseMenu: () => void
  moreDropdownComponent?: ((onCloseMenu: () => void) => ReactNode) | null
  moreActions: Array<ActionItem>
  actionsByGroup?: ActionGroup[]
  groupOrder?: string[]
  enableGrouping?: boolean
  enableSearch?: boolean
}

export const ActionsCustomComponent = ({
  setOnCloseMoreDropdown,
  onCloseMenu,
  moreDropdownComponent,
  moreActions,
  actionsByGroup,
  groupOrder,
  enableGrouping = false,
  enableSearch = false,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState('')
  const sortActionsByOrder = (actions: ActionItem[]): ActionItem[] => {
    return [...actions].sort((a, b) => {
      const orderA = a.sortOrder ?? Infinity
      const orderB = b.sortOrder ?? Infinity

      return orderA - orderB
    })
  }

  useEffect(() => {
    setOnCloseMoreDropdown &&
      setOnCloseMoreDropdown(() => {
        onCloseMenu()
      })
  }, [])

  const filterItems = useMemo(() => {
    if (!enableSearch || !searchQuery.trim()) return moreActions

    const query = searchQuery.toLowerCase().trim()

    return moreActions.filter((action) => action.text?.toLowerCase().includes(query))
  }, [moreActions, searchQuery, enableSearch])

  const ungroupedActions = useMemo(() => {
    let filtered: ActionItem[]

    if (!enableGrouping) {
      filtered = filterItems
    } else if (!groupOrder || !groupOrder.length) {
      filtered = filterItems.filter((action) => !action.group)
    } else {
      const groupOrderSet = new Set(groupOrder)

      filtered = filterItems.filter((action) => {
        if (!action.group) return true

        return !groupOrderSet.has(action.group)
      })
    }

    return sortActionsByOrder(filtered)
  }, [filterItems, enableGrouping, groupOrder])

  const groupItems = useMemo(() => {
    if (!enableGrouping) return

    if (actionsByGroup) {
      const result: ActionGroup[] = []

      for (const [groupName, originalItems] of actionsByGroup) {
        const visibleItems = originalItems.filter((item) => filterItems.includes(item))

        if (visibleItems.length) {
          result.push([groupName, sortActionsByOrder(visibleItems)])
        }
      }

      return result
    }

    const grouped = groupBy(filterItems, 'group')
    const result: ActionGroup[] = []

    for (const [groupName, items] of Object.entries(grouped)) {
      if (groupName !== 'undefined' && items.length) {
        result.push([groupName, sortActionsByOrder(items)])
      }
    }

    return result
  }, [enableGrouping, actionsByGroup, filterItems])

  if (moreDropdownComponent) {
    return <div className={styles.wrap}>{moreDropdownComponent(onCloseMenu)}</div>
  }

  const hasGroups = groupItems && groupItems.length > 0
  const hasUngrouped = ungroupedActions.length > 0
  const hasAnyActions = hasGroups || hasUngrouped

  return (
    <div className={styles.wrap}>
      {enableSearch && (
        <div className={styles.searchContainer}>
          <TextField
            size={'small'}
            onChange={setSearchQuery}
            variant={'integrated'}
            value={searchQuery}
            InputProps={{
              placeholder: 'Search',
              className: styles.searchInput,
            }}
          />
        </div>
      )}
      {hasAnyActions ? (
        <div className={styles.content} aria-label={getAriaLabel('CustomDropdownActions')}>
          {hasUngrouped &&
            ungroupedActions.map((action, index) => (
              <Action
                key={action.key ?? `ungrouped-${index}`}
                fromDropdown={true}
                onCloseMenu={onCloseMenu}
                action={action}
              />
            ))}

          {hasUngrouped && hasGroups && <div className={styles.divider} />}

          {hasGroups &&
            groupItems.map(([groupName, items]) => (
              <div key={groupName} className={styles.group}>
                <div className={styles.groupHeader}>
                  <Typography
                    variant={'body-sm-medium'}
                    color={'var(--content-primary-tertiary)'}
                    ariaLabel={'ActionsGroupTitle'}
                  >
                    {groupName}
                  </Typography>
                </div>
                <div className={styles.groupItems}>
                  {items.map((action, index) => (
                    <Action
                      key={action.key ?? `${groupName}-${index}`}
                      fromDropdown={true}
                      onCloseMenu={onCloseMenu}
                      action={action}
                    />
                  ))}
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div className={styles.emptyState}>No results found</div>
      )}
    </div>
  )
}
